import { Suspense } from "react"
import { GetServerSideProps, NextPage } from "next"
import { Container, Row, Spinner } from "react-bootstrap"
import dynamic from "next/dynamic"
import Head from "next/head"
import { ErrorBoundary } from "@sentry/nextjs"
import { Page } from "@styled/dashboard"
import { useCookies } from "react-cookie"
import { initializeApollo } from "apollo/client"
import Header from "@modules/Common/components/Header"
import ActivitiesList from "@modules/Activity/components/ActivitiesList"
import { DEFAULT_META_IMAGE_URL } from "@utils/constant"
import SkillsList from "@modules/Activity/components/SkillsList"
import DynamicActivitiesList from "@modules/Activity/components/DynamicActivitiesList"
import { PublicActivityPageQueriesQuery, PublicActivityPageQueriesQueryVariables, useStudentSkillsQuery } from "generated/graphql"
import { PUBLIC_ACTIVITY_PAGE_QUERIES } from "@modules/Activity/graphql"
import ErrorFallback from "@modules/Common/components/ErrorFallback"
const ActivityBanners = dynamic(() => import("@modules/Activity/components/ActivityBanners"))

interface IActivityIndexPageProps {
  isPublic: boolean
  publicActivitySkills?: PublicActivityPageQueriesQuery["publicActivitySkills"]
  publicLiveActivities?: PublicActivityPageQueriesQuery["publicLiveActivities"]
  publicNewReleaseActivities?: PublicActivityPageQueriesQuery["publicChallengeActivities"]
  publicChallengeActivities?: PublicActivityPageQueriesQuery["publicNewReleaseActivities"]
  publicPopularActivities?: PublicActivityPageQueriesQuery["publicPopularActivities"]
  error?: boolean
}

const ActivityIndexPage: NextPage<IActivityIndexPageProps> = (props) => {
  const [cookies] = useCookies(["idToken"])

  const title = "Explore Activities - 100s of New Activities Every Month | MySkillShaala"
  const description = "Learn new skills and build your confidence. My Skill Shaala provides hundreds of free fun, new activities designed to help you build skills, develop confidence and have fun. See the list of activities here."
  const image = DEFAULT_META_IMAGE_URL

  const { data: studentSkillsData } = useStudentSkillsQuery({
    variables: {
      skip: 0,
      take: 4
    },
    skip: !cookies.idToken,
    fetchPolicy: "cache-and-network"
  })

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="title" content={title} />
        <meta name="description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${process.env.NEXT_PUBLIC_SITE_URL}/activities`} />
        <meta property="og:site_name" content={process.env.NEXT_PUBLIC_SITE_URL} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={image} />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={`${process.env.NEXT_PUBLIC_SITE_URL}/activities`} />
        <meta property="twitter:title" content={title} />
        <meta property="twitter:description" content={description} />
        <meta property="twitter:image" content={image} />
      </Head>
      <Page>
        <Header />
        <Container style={{ textAlign: "left" }}>
          <ErrorBoundary fallback={ErrorFallback}>
            <ActivityBanners />
          </ErrorBoundary>
          {props.isPublic ? <div style={{ paddingTop: "36px" }}>
            {props.publicNewReleaseActivities && props.publicNewReleaseActivities?.length > 0 && <ActivitiesList activities={props.publicNewReleaseActivities} title="New Activities" showButton="play" description="Here are some new activities that we specially curated for you." />}
            {props.publicChallengeActivities && props.publicChallengeActivities?.length > 0 && <ActivitiesList activities={props.publicChallengeActivities} title="Challenges" description="Here are few quick challenges to help you earn more XPs." />}
            {props.publicActivitySkills && props.publicActivitySkills?.length > 0 && <SkillsList skills={props.publicActivitySkills} title="Skills" description="<p>Want to learn a particular skill? Check activities by skills here</p>" />}
            {props.publicPopularActivities && props.publicPopularActivities?.length > 0 && <ActivitiesList activities={props.publicPopularActivities} title="Popular Activities" showButton="play" description="<p>Here are few activities and challenges that are most famous within our students.</p>" />}
            {props.publicLiveActivities && props.publicLiveActivities?.length > 0 && <ActivitiesList activities={props.publicLiveActivities} title="Live Activities" showButton="live" description="<p>Join fun-filled activities with LIVE facilitators.</p>" />}
          </div> : <div style={{ paddingTop: "36px" }}>
            <DynamicActivitiesList where={{ isPending: true }} title="Pending Activities" showButton="play" description="<p>Complete the challenges in this activities and submit your photo or video to earn XPs.</p>" />
            <DynamicActivitiesList where={{ isNewReleased: true }} title="New Activities" showButton="play" description="<p>Here are some new activities that we specially curated for you.</p>" />
            {studentSkillsData && studentSkillsData.studentSkills?.length > 0 && <SkillsList skills={studentSkillsData.studentSkills} title="Skills" description="<p>Want to learn a particular skill? Check activities by skills here</p>" />}
            <DynamicActivitiesList where={{ isChallenge: true }} title="Challenges" description="<p>Here are few quick challenges to help you earn more XPs.</p>" />
            <DynamicActivitiesList where={{ isPopular: true }} title="Popular Activities" showButton="play" description="<p>Here are few activities and challenges that are most famous within our students.</p>" />
            <DynamicActivitiesList where={{ isLive: true }} title="Live Activities" showButton="live" description="<p>Join fun-filled activities with LIVE facilitators.</p>" />
          </div>}
        </Container>
      </Page>
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async ({ req: { cookies }, res }) => {
  if (cookies["idToken"]) {
    return {
      props: {
        isPublic: false,
      }
    }
  }
  else {
    const apolloClient = initializeApollo()
    try {
      const { data, error } = await apolloClient.query<PublicActivityPageQueriesQuery, PublicActivityPageQueriesQueryVariables>({
        query: PUBLIC_ACTIVITY_PAGE_QUERIES,
        variables: {
          publicLiveActivitiesWhere: {
            isLive: true
          },
          publicChallengeActivitiesWhere: {
            isChallenge: true
          },
          publicNewReleaseActivitiesWhere: {
            isNewReleased: true
          },
          publicPopularActivitiesWhere: {
            isPopular: true
          }
        }
      })
      if (data) {
        res.setHeader(
          "Cache-Control",
          "public, s-maxage=300"
        )
        return {
          props: {
            ...data,
            isPublic: true,
            initialApolloState: apolloClient.cache.extract(),
          },
        }
      } else {
        return {
          props: {
            error: true
          }
        }
      }
    } catch (error: any) {
      return {
        props: {
          error: true
        }
      }
    }
  }
}

export default ActivityIndexPage
